// Trust Settings Components
import AjaxUtils from "../../util/ajax_utils";
import {toKebabCase} from "../_ui/_utils/useStringUtils";
import {delay} from "../_ui/_utils/useUiUtils";
import RecipientPicker from './RecipientPicker.vue';

const ApckCountrySelector = {
    props: {
        label: {
            type: String,
            default: "Countries"
        },
        description: {
            type: String,
        },
        modelValue: {
            type: Array,
            default: function() {
                return [];
            }
        },
        disabled: {
            type: Boolean,
            default: false
        }
    },
    data: function() {
        return {
            loading: false,
            availableCountries: [],
            pendingSelection: [],
        };
    },
    computed: {
        selectedCountries() {
            return this.availableCountries
                .filter(country => this.modelValue.includes(country.code));
        },
        dropdownLabel() {
            return `Select${this.selectedCountries.length > 0 ? ' more' : ''} countries`
        },
        dropdownId() {
            return `dropdown-${this._uid}`
        },
        dropdownName() {
            return toKebabCase(this.label)
        },
        countryOptions() {
            return this.availableCountries
                .filter(country => !this.modelValue.includes(country.code))
                .map(({ name, code }) => ({
                    label: name,
                    value: code
                }));
        }
    },
    methods: {
        loadCountries: function() {
            let cmp = this;
            cmp.loading = true;

            AjaxUtils.ajax_get({
                url: '/trust/settings/iso-3166-1',
                success: function(data) {
                    if (data.success) {
                        cmp.availableCountries = data.data;
                    }
                    cmp.loading = false;
                },
                fail: function(data) {
                    console.error('Failed to load countries:', data);
                    cmp.loading = false;
                },
                opts: {
                    dataType: 'json',
                    flash_form_errors: false,
                    flash_404_errors: false,
                    show_success_flash: false
                }
            });
        },
        removeCountry: function(countryCode) {
            let newValue = this.modelValue.filter(function(code) {
                return code !== countryCode;
            });
            this.$emit('update:modelValue', newValue);
        },
        getFlagImageUrl: function(countryCode) {
            let code = countryCode.toLowerCase();
            return `/assets/images/svg_flags/${code}.svg`;
        },
        onDropdownChange(selectedCodes) {
            this.pendingSelection = selectedCodes;
        },
        applySelection() {
            if (this.pendingSelection.length) {
                const newValue = [...new Set([...this.modelValue, ...this.pendingSelection])];
                this.$emit('update:modelValue', newValue);
                this.pendingSelection = [];
            }
        },
        clearSelection() {
            this.pendingSelection = [];
        },
        removeAllCountries() {
            this.$emit('update:modelValue', []);
            this.pendingSelection = [];
        }
    },
    mounted: function() {
        this.loadCountries();
    },
    template: `
        <ui-layout direction="column" align="start" justify="start" gap="2">
            <ui-heading v-if="label" tag="span" color="secondary">
                {{ label }}
            </ui-heading>

            <ui-layout direction="column">
                <ui-layout v-if="selectedCountries.length" align="center" wrap>
                    <ui-layout v-for="country in selectedCountries" :key="country.code" gap="2" align="center">
                        <img :src="getFlagImageUrl(country.code)" width="32" height="auto" :alt="country.name" />

                        <span class="text--secondary">{{ country.name }}</span>

                        <ui-button
                            :label="'Remove ' + country.name"
                            :title="'Remove ' + country.name"
                            hideLabel
                            type="outline"
                            color="red"
                            size="sm"
                            iconLeft="fa-xmark"
                            autoHeight
                            :disabled="disabled"
                            @click.prevent="removeCountry(country.code)"
                        />
                    </ui-layout>
                </ui-layout>

                <ui-layout gap="2" align="center">
                    <ui-dropdown-checkbox
                        :id="dropdownId"
                        :name="dropdownName"
                        :modelValue="pendingSelection"
                        :options="countryOptions"
                        :label="dropdownLabel"
                        align="left"
                        :loading="disabled"
                        @update:modelValue="onDropdownChange"
                    />

                    <ui-button
                        label="Add"
                        :disabled="disabled || !pendingSelection.length"
                        @click.prevent="applySelection"
                    />

                    <ui-button
                        label="Clear selection"
                        color="red"
                        :disabled="disabled || !pendingSelection.length"
                        @click.prevent="clearSelection"
                    />

                    <ui-button
                        label="Remove all"
                        type="outline"
                        color="red"
                        :disabled="disabled || !selectedCountries.length"
                        @click.prevent="removeAllCountries"
                    />
                </ui-layout>
            </ui-layout>
            
            <span v-if="description" class="text--secondary text-sm">
                {{description}}    
            </span>
        </ui-layout>
    `
};

const ApckRiskLevelSelector = {
    props: {
        label: {
            type: String,
            default: "Trust Levels"
        },
        modelValue: {
            type: Array,
            default: function() {
                return [];
            }
        },
        disabled: {
            type: Boolean,
            default: false
        }
    },
    data: function() {
        return {
            loading: false,
            availableRiskLevels: [],
            selectedRiskLevels: this.modelValue
        };
    },
    methods: {
        loadRiskLevels: function() {
            let cmp = this;
            cmp.loading = true;

            AjaxUtils.ajax_get({
                url: '/trust/settings/risks',
                success: function(data) {
                    if (data.success) {
                        cmp.availableRiskLevels = data.data;
                    }
                    cmp.loading = false;
                },
                fail: function(data) {
                    console.error('Failed to load risk levels:', data);
                    cmp.loading = false;
                },
                opts: {
                    dataType: 'json',
                    flash_form_errors: false,
                    flash_404_errors: false,
                    show_success_flash: false
                }
            });
        },
        toggleRiskLevel(risk) {
            const riskExists = this.selectedRiskLevels.some(item => item === risk.level);
            if(riskExists) {
                this.selectedRiskLevels = this.selectedRiskLevels.filter(item => item !== risk.level);
            } else {
                this.selectedRiskLevels.push(risk.level);
            }
        },
        isRiskActive(risk) {
            return this.selectedRiskLevels.some(item => item === risk.level);
        },
        trustColor(level) {
            const colors = {
                0: 'red',
                1: 'orange',
                2: 'yellow',
                3: 'green',
                4: 'blue',
                5: 'light-slate'
            };
            return colors[level] || '';
        },
    },
    mounted: function() {
        this.loadRiskLevels();
    },
    watch: {
        selectedRiskLevels: {
            handler(newVal) {
                this.$emit('update:modelValue', newVal);
            },
            deep: true
        },
        modelValue(newVal) {
            this.selectedRiskLevels = newVal
        }
    },
    template: `
        <ui-layout gap="2" direction="column" align="start" justify="start" class="offset-left">
            <ui-heading tag="span" color="secondary">
                {{ label }}
            </ui-heading>

            <ui-layout align="center" wrap>
                <ui-button
                    v-for="(risk, index) in availableRiskLevels"
                    :key="risk.level"
                    :label="risk.name === 'Critical' ? 'Highest' : risk.name"
                    :type="isRiskActive(risk) ? 'primary' : 'outline'"
                    :color="trustColor(index)"
                    :iconRight="isRiskActive(risk) ? 'fa-circle-xmark' : 'fa-circle-plus'"
                    :disabled="disabled || loading"
                    mutedColor
                    @click.prevent="toggleRiskLevel(risk)"
                />
            </ui-layout>

            <span class="text--secondary text-sm">Select which trust levels should trigger alerts</span>
        </ui-layout>
    `
};

const ApckDaysSelector = {
    props: {
        label: {
            type: String,
            default: ''
        },
        description: {
            type: String,
            default: ''
        },
        modelValue: {
            type: Number,
            default: 30
        },
        disabled: {
            type: Boolean,
            default: false
        }
    },
    data: function() {
        return {
            dayOptions: [
                { value: 1, label: "1 day" },
                { value: 3, label: "3 days" },
                { value: 7, label: "7 days" },
                { value: 14, label: "14 days" },
                { value: 30, label: "30 days" },
                { value: 60, label: "60 days" },
                { value: 90, label: "90 days" },
                { value: 120, label: "120 days" },
                { value: 180, label: "180 days" },
                { value: 365, label: "365 days" }
            ],
            selectedValue: this.modelValue
        };
    },
    computed: {
        dropdownLabel() {
            const option = this.dayOptions.find(
                (opt) => opt.value === this.selectedValue
            )
            return option ? option.label : ''
        },
        dropdownId() {
            return `dropdown-${this._uid}`
        },
        dropdownName() {
            return toKebabCase(this.label)
        },
    },
    methods: {
        onSelectionChange(value) {
            this.selectedValue = value;
            this.$emit('update:modelValue', value)
        },
    },
    watch: {
        modelValue(newVal) {
            this.selectedValue = newVal
        }
    },
    template: `
        <ui-layout direction="column" align="start" justify="start" gap="2">
            <ui-heading v-if="label" tag="span" color="secondary">
                {{ label }}
            </ui-heading>

            <ui-dropdown-radio
                :id="dropdownId"
                :name="dropdownName"
                :modelValue="selectedValue"
                :options="dayOptions"
                :label="dropdownLabel"
                align="left"
                :loading="disabled"
                @update:modelValue="onSelectionChange"
            />

            <span class="text--secondary text-sm">
                {{description}}    
            </span>
        </ui-layout>
    `
};

const ApckSettingsPanel = {
    props: {
        title: {
            type: String,
            required: true,
        },
        loading: {
            type: Boolean,
            default: true
        }
    },
    template: `
        <ui-portlet-base
            :title="title"
            :loading="loading"
            titlePadding="md"
        >
            <ui-panel-base
                direction="column"
                justify="start"
                align="start"
                gap="4"
                class="trust-settings-panel"
            >
                <slot />
            </ui-panel-base>
        </ui-portlet-base>
    `
}

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const ApckScheduleWindowEditor = {
    props: {
        modelValue: {
            type: Array,
            default: function() { return []; }
        },
        disabled: {
            type: Boolean,
            default: false
        }
    },
    data: function() {
        return {
            windows: JSON.parse(JSON.stringify(this.modelValue || []))
        };
    },
    watch: {
        modelValue: {
            handler: function(newVal) {
                this.windows = JSON.parse(JSON.stringify(newVal || []));
            },
            deep: true
        }
    },
    methods: {
        addWindow: function() {
            this.windows.push({ start: '22:00', end: '06:00', days: [0,1,2,3,4] });
            this.emitChange();
        },
        removeWindow: function(index) {
            this.windows.splice(index, 1);
            this.emitChange();
        },
        toggleDay: function(windowIndex, dayIndex) {
            var days = this.windows[windowIndex].days;
            var pos = days.indexOf(dayIndex);
            if (pos === -1) {
                days.push(dayIndex);
            } else {
                days.splice(pos, 1);
            }
            this.emitChange();
        },
        isDayActive: function(windowIndex, dayIndex) {
            return this.windows[windowIndex].days.indexOf(dayIndex) !== -1;
        },
        updateTime: function(windowIndex, field, value) {
            this.windows[windowIndex][field] = value;
            this.emitChange();
        },
        emitChange: function() {
            this.$emit('update:modelValue', JSON.parse(JSON.stringify(this.windows)));
        },
        dayNames: function() { return DAY_NAMES; }
    },
    template: `
        <ui-layout direction="column" gap="2">
            <ui-layout v-for="(win, wi) in windows" :key="wi" direction="column" gap="1" class="schedule-window-row">
                <ui-layout align="center" gap="2">
                    <label class="text--secondary text-sm">From</label>
                    <input type="time" class="form-control" style="max-width:120px"
                           :value="win.start" :disabled="disabled"
                           @input="updateTime(wi, 'start', $event.target.value)" />
                    <label class="text--secondary text-sm">To</label>
                    <input type="time" class="form-control" style="max-width:120px"
                           :value="win.end" :disabled="disabled"
                           @input="updateTime(wi, 'end', $event.target.value)" />
                    <ui-button label="Remove" type="outline" color="red" size="sm"
                               :disabled="disabled" @click.prevent="removeWindow(wi)" />
                </ui-layout>
                <ui-layout align="center" gap="1">
                    <ui-button v-for="(name, di) in dayNames()" :key="di"
                               :label="name" size="sm"
                               :type="isDayActive(wi, di) ? 'primary' : 'outline'"
                               :disabled="disabled"
                               @click.prevent="toggleDay(wi, di)" />
                </ui-layout>
            </ui-layout>
            <ui-layout>
                <ui-button label="Add Window" iconLeft="fa-plus" type="outline" size="sm"
                           :disabled="disabled" @click.prevent="addWindow" />
            </ui-layout>
        </ui-layout>
    `
};

export const ApckTrustSettings = {
    components: {
        'apck-settings-panel': ApckSettingsPanel,
        'apck-country-selector': ApckCountrySelector,
        'apck-risk-level-selector': ApckRiskLevelSelector,
        'apck-days-selector': ApckDaysSelector,
        'apck-schedule-window-editor': ApckScheduleWindowEditor,
        'apck-recipient-picker': RecipientPicker,
    },
    data: function() {
        return {
            activeTab: 'alerts',
            tabs: [
                { id: 'alerts',      title: 'Alerts' },
                { id: 'activemonitor', title: 'Active Monitor' },
                { id: 'delivery',    title: 'Delivery' },
                { id: 'webhook',     title: 'Webhook' },
                { id: 'suppression', title: 'Suppression' },
                { id: 'scan',        title: 'Scan Settings' },
            ],
            loading: false,
            saving: false,
            webhookTesting: false,
            processEventsProcessing: false,
            usersLoading: false,
            orgUsers: [],
            settings: {
                enable_alert_on_risks: false,
                alert_risk_list: [],
                enable_alert_on_counteries: false,
                alert_country_list: [],
                enable_alert_on_new_vulnerabilities: false,
                enable_alert_on_new_thirdparties: false,
                enable_alert_on_geolocation_changes: false,
                enable_alert_on_new_geolocations: false,
                enable_alert_on_domain_expiration: false,
                domain_expiration_warning_days: 30,
                enable_alert_on_new_recently_registered: false,
                recently_registered_threshold_days: 90,
                raise_alerts_on_last_n_days: 30,
                enable_suppression: true,
                enable_osint_discovery: false,
                enable_osint_scanning: false,
                include_dns_bruteforce: false,
                max_osint_hosts: 100,
                enable_all_osint_sources: false,
                apply_to_all_http_responses: false,
                trustmonitor_crawl_depth: 100,
                trustmonitor_crawl_maxtime: 600,
                baseline_date: null,
                delivery_frequency: 'immediate',
                webhook_enabled: false,
                webhook_url: '',
                webhook_auth_type: 'hmac',
                webhook_secret: '',
                webhook_secret_set: false,
                alert_recipient_user_ids: [],
                active_monitor_alerts_enabled: true,
                active_monitor_alert_recipient_user_ids: [],
                active_monitor_alert_on_resource_added: true,
                active_monitor_alert_on_resource_changed: true,
                active_monitor_alert_on_frame_changed: true,
                active_monitor_max_alerts_per_hour: 10,
                active_monitor_request_header: '',
                active_monitor_request_header_name: 'appcheck-active-monitor',
                scan_repeat_interval: '',
                next_scan_at: null,
                scan_schedule_times: [],
                scan_repeat_options: [],
            }
        };
    },
    computed: {
        deliveryOptions: function() {
            return [
                { value: 'immediate', label: 'Immediate' },
                { value: 'daily',     label: 'Daily digest' },
                { value: 'weekly',    label: 'Weekly digest' },
            ];
        },
        authTypeOptions: function() {
            return [
                { value: 'hmac',   label: 'HMAC-SHA256 signature' },
                { value: 'bearer', label: 'Bearer token' },
            ];
        },
        selectedUserEmails: function() {
            var ids = this.settings.alert_recipient_user_ids || [];
            if (!ids.length) return [];
            var cmp = this;
            return cmp.orgUsers.filter(function(u) { return ids.indexOf(u.id) !== -1; });
        },
        /**
         * Paste-ready instructions for the customer's WAF administrator.
         *
         * Built from the value actually saved, so what they paste and what we send cannot
         * disagree - a rule matching a typo of the secret fails in exactly the way this
         * feature exists to prevent, and it fails silently.
         */
        requestHeaderInstructions: function() {
            var name = this.settings.active_monitor_request_header_name
                || 'appcheck-active-monitor';
            var value = this.settings.active_monitor_request_header || '';
            return [
                'AppCheck Active Monitor sends this header on every request:',
                '',
                '    ' + name + ': ' + value,
                '',
                'Cloudflare (Security > WAF > Custom rules), placed first in the order:',
                '',
                '    (http.request.headers["' + name + '"][0] eq "' + value + '")',
                '',
                'Action: Skip. Tick all of:',
                '  - All remaining custom rules',
                '  - WAF Managed Rules (and the OWASP ruleset if enabled)',
                '  - Super Bot Fight Mode / Bot Management',
                '  - Rate limiting rules',
                '  - Browser Integrity Check',
                '  - Security Level',
                '  - User Agent Blocking, Zone Lockdown, IP Access Rules',
                '',
                'Narrow it to the monitored hostnames by adding, for example:',
                '    and http.host in {"www.example.com" "example.com"}',
                '',
                'Deploy, then check Security > Events that requests carrying the header',
                'show as skipped. If a WAF, rate limiter or bot protection also runs at',
                'your origin behind Cloudflare, allowlist the same header there too.',
            ].join('\n');
        },
    },
    methods: {
        switchTab: function(tab) {
            this.activeTab = tab.id;
        },
        /**
         * A random UUID for the allowlist header.
         *
         * Deliberately NOT util/helpers.createUUIDv4: that one is built on Math.random(),
         * which is fine for a DOM id and wrong for a value whose entire job is being
         * unguessable - anything that can predict this can bypass the customer's WAF.
         *
         * Hyphens are safe here: the value ends up inside a double-quoted WAF expression,
         * and it is only quotes and backslashes that would break one.
         */
        generateRequestHeader: function() {
            var uuid;
            if (window.crypto && typeof window.crypto.randomUUID === 'function') {
                uuid = window.crypto.randomUUID();
            } else {
                // Same v4 layout from the CSPRNG, for browsers that have
                // getRandomValues but not randomUUID.
                var bytes = new Uint8Array(16);
                window.crypto.getRandomValues(bytes);
                bytes[6] = (bytes[6] & 0x0f) | 0x40;   // version 4
                bytes[8] = (bytes[8] & 0x3f) | 0x80;   // variant 10xx
                var hex = [];
                for (var i = 0; i < bytes.length; i++) {
                    hex.push((bytes[i] + 0x100).toString(16).slice(1));
                }
                uuid = [
                    hex.slice(0, 4).join(''),
                    hex.slice(4, 6).join(''),
                    hex.slice(6, 8).join(''),
                    hex.slice(8, 10).join(''),
                    hex.slice(10, 16).join(''),
                ].join('-');
            }
            this.updateSetting('active_monitor_request_header', uuid);
        },
        async loadSettings() {
            this.loading = true;
            var cmp = this;

            await AjaxUtils.ajax_get({
                url: '/trust/settings/view',
                success: function(data) {
                    if (data.success) {
                        cmp.settings = data.data;
                    } else {
                        cmp.$flash({message: data.message, type: 'error'});
                    }
                },
                fail: function() {
                    cmp.$flash({message: 'Failed to load trust settings', type: 'error'});
                },
                opts: {
                    dataType: 'json',
                    flash_form_errors: false,
                    flash_404_errors: false,
                    show_success_flash: false
                }
            });

            await delay(100);
            this.loading = false;
        },
        loadUsers: function() {
            var cmp = this;
            cmp.usersLoading = true;
            AjaxUtils.ajax_get({
                url: '/trust/settings/users',
                success: function(data) {
                    if (data.success) {
                        cmp.orgUsers = data.data;
                    }
                    cmp.usersLoading = false;
                },
                fail: function() {
                    cmp.usersLoading = false;
                },
                opts: { dataType: 'json', flash_form_errors: false, flash_404_errors: false, show_success_flash: false }
            });
        },
        saveSettings: function() {
            var cmp = this;
            cmp.saving = true;

            AjaxUtils.ajax_post({
                url: '/trust/settings/update',
                data: JSON.stringify(cmp.settings),
                success: function(data) {
                    cmp.saving = false;
                    if(data.success) {
                        // Silent save — no flash on success
                    } else if (data.warnings && data.warnings.length) {
                        data.warnings.forEach(function(warning) {
                            cmp.$flash({message: warning, type: 'warning'});
                        });
                    } else {
                        cmp.$flash({message: data.message, type: 'error'});
                    }
                },
                fail: function() {
                    cmp.saving = false;
                    cmp.$flash({message: 'Failed to save trust settings', type: 'error'});
                },
                opts: {
                    dataType: 'json',
                    contentType: 'application/json',
                    flash_form_errors: false,
                    flash_404_errors: false
                }
            });
        },
        async resetSettings() {
            await this.loadSettings();
        },
        updateSetting: function(setting, value) {
            if(!this.settings.hasOwnProperty(setting)) {
                console.warn(setting + ' does not exist in settings.');
                return;
            }
            this.settings[setting] = value;
            this.debouncedSave();
        },
        testWebhook: function() {
            var cmp = this;
            cmp.webhookTesting = true;
            AjaxUtils.ajax_post({
                url: '/trust/settings/test-webhook',
                data: '{}',
                success: function(data) {
                    cmp.webhookTesting = false;
                    if (data.success) {
                        cmp.$flash({message: 'Webhook test succeeded (HTTP ' + data.status_code + ')', type: 'success'});
                    } else {
                        cmp.$flash({message: data.message || 'Webhook test failed', type: 'error'});
                    }
                },
                fail: function() {
                    cmp.webhookTesting = false;
                    cmp.$flash({message: 'Webhook test request failed', type: 'error'});
                },
                opts: { dataType: 'json', contentType: 'application/json', flash_form_errors: false, flash_404_errors: false }
            });
        },
        processEventsNow: function() {
            var cmp = this;
            cmp.processEventsProcessing = true;
            AjaxUtils.ajax_post({
                url: '/trust/settings/process-events',
                data: '{}',
                success: function(data) {
                    cmp.processEventsProcessing = false;
                    if (data.success) {
                        cmp.$flash({message: data.message || 'Events queued for processing', type: 'success'});
                    } else {
                        cmp.$flash({message: data.message || 'Failed to process events', type: 'error'});
                    }
                },
                fail: function() {
                    cmp.processEventsProcessing = false;
                    cmp.$flash({message: 'Failed to queue event processing', type: 'error'});
                },
                opts: { dataType: 'json', contentType: 'application/json', flash_form_errors: false, flash_404_errors: false }
            });
        },
        // Both recipient tables save through here. The picker hands back a new array
        // rather than mutating in place, which is what makes the assignment reactive -
        // the previous push()-into-the-existing-array version relied on the reassignment
        // that followed it.
        setRecipients: function(setting, ids) {
            this.settings[setting] = ids;
            this.debouncedSave();
        },
        debouncedSave: function() {
            if (!this._settingsReady) return;
            var cmp = this;
            if (cmp._saveTimer) clearTimeout(cmp._saveTimer);
            cmp._saveTimer = setTimeout(function() {
                cmp.saveSettings();
            }, 600);
        },
    },
    async mounted() {
        this._settingsReady = false;
        await this.loadSettings();
        this.loadUsers();
        var cmp = this;
        this.$nextTick(function() {
            cmp._settingsReady = true;
        });
        // Support deep-linking to a specific tab via ?tab=xxx
        var urlParams = new URLSearchParams(window.location.search);
        var tabParam = urlParams.get('tab');
        if (tabParam) {
            var match = cmp.tabs.find(function(t) { return t.id === tabParam; });
            if (match) {
                cmp.activeTab = match.id;
            }
        }
    },
    template: `
        <ui-page title="Trust Monitor Settings" stickyTitle>
            <template #title-bar-buttons>
                <ui-layout align="center" gap="1">
                    <ui-spinner v-if="saving" :show="true" :fill="false" relative />
                    <span v-if="saving" class="text--secondary text-sm">Saving…</span>
                </ui-layout>
            </template>
            <template #content>
                <ui-layout direction="column" gap="0">
                    <!-- Tab bar -->
                    <ui-layout direction="row" gap="0" align="end">
                        <ui-button
                            v-for="tab in tabs"
                            :key="tab.id"
                            :label="tab.title"
                            :type="activeTab === tab.id ? 'primary' : 'outline'"
                            tab
                            @click.prevent="switchTab(tab)"
                        />
                    </ui-layout>

                    <!-- ======== ALERTS TAB ======== -->
                    <ui-panel-base
                        v-if="activeTab === 'alerts'"
                        direction="column"
                        justify="start"
                        align="stretch"
                        gap="2"
                        class="panel-box--tab-content"
                    >
                        <apck-settings-panel title="Baseline Date" :loading="loading">
                            <ui-layout direction="column" gap="2">
                                <span class="text--secondary" style="font-size: 13px;">
                                    Only raise alerts for trust relationships discovered after this date.
                                    Leave empty to consider all data.
                                </span>
                                <input
                                    type="date"
                                    class="form-control"
                                    style="max-width: 200px;"
                                    :value="settings.baseline_date ? settings.baseline_date.split('T')[0] : ''"
                                    :disabled="saving"
                                    @change="updateSetting('baseline_date', $event.target.value)"
                                />
                            </ui-layout>
                        </apck-settings-panel>

                        <apck-settings-panel title="Alert Time" :loading="loading">
                            <apck-days-selector
                                label="Alert Time Window"
                                description="Configure how far back to check for trust relationships when raising alerts"
                                :modelValue="settings.raise_alerts_on_last_n_days || null"
                                :disabled="saving"
                                @update:modelValue="updateSetting('raise_alerts_on_last_n_days', $event)"
                            />
                        </apck-settings-panel>

                        <apck-settings-panel title="General Alerts" :loading="loading">
                            <ui-layout direction="column">
                                <ui-input-checkbox
                                    label="Enable Trust Level Alerts"
                                    description="Enable alerts when trust relationships match specified trust levels"
                                    labelSize="base"
                                    :disabled="saving"
                                    :modelValue="settings.enable_alert_on_risks"
                                    @update:modelValue="updateSetting('enable_alert_on_risks', $event)"
                                />
                                <ui-expandable-content
                                    :open="settings.enable_alert_on_risks"
                                    id="risklevels"
                                    contentId="risklevels_content"
                                >
                                    <apck-risk-level-selector
                                        label="Alert Trust Levels"
                                        :modelValue="settings.alert_risk_list"
                                        :disabled="saving"
                                        @update:modelValue="updateSetting('alert_risk_list', $event)"
                                    />
                                </ui-expandable-content>
                            </ui-layout>

                            <ui-input-checkbox
                                label="Enable New Vulnerability Alerts"
                                description="Enable alerts when new vulnerabilities are identified in trust relationships"
                                labelSize="base"
                                :disabled="saving"
                                :modelValue="settings.enable_alert_on_new_vulnerabilities"
                                @update:modelValue="updateSetting('enable_alert_on_new_vulnerabilities', $event)"
                            />

                            <ui-input-checkbox
                                label="Enable New Third Party Alerts"
                                description="Enable alerts when new third parties are detected"
                                labelSize="base"
                                :disabled="saving"
                                :modelValue="settings.enable_alert_on_new_thirdparties"
                                @update:modelValue="updateSetting('enable_alert_on_new_thirdparties', $event)"
                            />
                        </apck-settings-panel>

                        <apck-settings-panel title="Location Alerts" :loading="loading">
                            <ui-layout direction="column">
                                <ui-input-checkbox
                                    label="Enable Country Alerts"
                                    description="Enable alerts when trust relationships involve specified countries"
                                    labelSize="base"
                                    :disabled="saving"
                                    :modelValue="settings.enable_alert_on_counteries"
                                    @update:modelValue="updateSetting('enable_alert_on_counteries', $event)"
                                />
                                <ui-expandable-content
                                    :open="settings.enable_alert_on_counteries"
                                    id="countrySelector"
                                    contentId="countrySelector_content"
                                    class="offset-left"
                                >
                                    <apck-country-selector
                                        label="Alert Countries"
                                        description="Select which countries should trigger alerts"
                                        :modelValue="settings.alert_country_list"
                                        :disabled="saving"
                                        @update:modelValue="updateSetting('alert_country_list', $event)"
                                    />
                                </ui-expandable-content>
                            </ui-layout>

                            <ui-input-checkbox
                                label="Enable Geolocation Change Alerts"
                                description="Enable alerts when third parties change geolocation"
                                labelSize="base"
                                :disabled="saving"
                                :modelValue="settings.enable_alert_on_geolocation_changes"
                                @update:modelValue="updateSetting('enable_alert_on_geolocation_changes', $event)"
                            />

                            <ui-input-checkbox
                                label="Enable New Geolocation Alerts"
                                description="Enable alerts when new geolocations are detected"
                                labelSize="base"
                                :disabled="saving"
                                :modelValue="settings.enable_alert_on_new_geolocations"
                                @update:modelValue="updateSetting('enable_alert_on_new_geolocations', $event)"
                            />
                        </apck-settings-panel>

                        <apck-settings-panel title="Domain Alerts" :loading="loading">
                            <ui-layout direction="column">
                                <ui-input-checkbox
                                    label="Enable Domain Expiration Alerts"
                                    description="Enable alerts when domains are nearing expiration"
                                    labelSize="base"
                                    :disabled="saving"
                                    :modelValue="settings.enable_alert_on_domain_expiration"
                                    @update:modelValue="updateSetting('enable_alert_on_domain_expiration', $event)"
                                />
                                <ui-expandable-content
                                    :open="settings.enable_alert_on_domain_expiration"
                                    id="domainexpiration"
                                    contentId="domainexpiration_content"
                                    class="offset-left"
                                >
                                    <apck-days-selector
                                        label="Domain Expiration Warning Days"
                                        description="Number of days before expiration to trigger alerts"
                                        :modelValue="settings.domain_expiration_warning_days || null"
                                        :disabled="saving"
                                        @update:modelValue="updateSetting('domain_expiration_warning_days', $event)"
                                    />
                                </ui-expandable-content>
                            </ui-layout>

                            <ui-layout direction="column">
                                <ui-input-checkbox
                                    label="Enable Recently Registered Domain Alerts"
                                    description="Enable alerts when new third parties use recently registered domains"
                                    labelSize="base"
                                    :disabled="saving"
                                    :modelValue="settings.enable_alert_on_new_recently_registered"
                                    @update:modelValue="updateSetting('enable_alert_on_new_recently_registered', $event)"
                                />
                                <ui-expandable-content
                                    :open="settings.enable_alert_on_new_recently_registered"
                                    id="recentlyregistered"
                                    contentId="recentlyregistered_content"
                                    class="offset-left"
                                >
                                    <apck-days-selector
                                        label="Recently Registered Threshold Days"
                                        description="Consider domains registered within this many days as 'recently registered'"
                                        :modelValue="settings.recently_registered_threshold_days || null"
                                        :disabled="saving"
                                        @update:modelValue="updateSetting('recently_registered_threshold_days', $event)"
                                    />
                                </ui-expandable-content>
                            </ui-layout>
                        </apck-settings-panel>
                    </ui-panel-base>

                    <!-- ======== ACTIVE MONITOR TAB ======== -->
                    <ui-panel-base
                        v-if="activeTab === 'activemonitor'"
                        direction="column"
                        justify="start"
                        align="stretch"
                        gap="2"
                    >
                        <apck-settings-panel title="Active Monitor Alerts" :loading="loading">
                            <ui-layout direction="column" gap="2">
                                <span class="text--secondary text-sm">
                                    Active Monitor watches the scripts and iframes on your payment
                                    pages. These settings control what you are told about, and who
                                    is told. They are separate from the Trust Monitor alerts on the
                                    other tabs.
                                </span>

                                <ui-input-checkbox
                                    label="Enable Active Monitor Alerts"
                                    description="Send email and in-app alerts when changes are detected"
                                    labelSize="base"
                                    :disabled="saving"
                                    :modelValue="settings.active_monitor_alerts_enabled"
                                    @update:modelValue="updateSetting('active_monitor_alerts_enabled', $event)"
                                />

                                <ui-expandable-content
                                    :open="settings.active_monitor_alerts_enabled"
                                    id="activeMonitorEvents"
                                    contentId="activeMonitorEvents_content"
                                    class="offset-left"
                                >
                                    <ui-layout direction="column" gap="2">
                                        <ui-input-checkbox
                                            label="New scripts and resources"
                                            description="A script that was not on the page at the last check is now loading"
                                            labelSize="base"
                                            :disabled="saving"
                                            :modelValue="settings.active_monitor_alert_on_resource_added"
                                            @update:modelValue="updateSetting('active_monitor_alert_on_resource_added', $event)"
                                        />

                                        <ui-input-checkbox
                                            label="Script content changes"
                                            description="A tracked script's content has changed and has not been reviewed. Scripts you have marked as expected to change are excluded."
                                            labelSize="base"
                                            :disabled="saving"
                                            :modelValue="settings.active_monitor_alert_on_resource_changed"
                                            @update:modelValue="updateSetting('active_monitor_alert_on_resource_changed', $event)"
                                        />

                                        <ui-input-checkbox
                                            label="Iframe changes"
                                            description="An iframe is new, has moved origin, or loads from somewhere not on your allow list"
                                            labelSize="base"
                                            :disabled="saving"
                                            :modelValue="settings.active_monitor_alert_on_frame_changed"
                                            @update:modelValue="updateSetting('active_monitor_alert_on_frame_changed', $event)"
                                        />

                                        <!-- Shown on and locked rather than hidden: a customer
                                             looking for the switch should find out why there
                                             isn't one, not wonder whether we alert at all. -->
                                        <ui-input-checkbox
                                            label="Integrity failures (always on)"
                                            description="A declared integrity hash no longer matches, or a resource could not be retrieved. This cannot be switched off: PCI DSS 11.6.1 expects tamper detection to alert your personnel."
                                            labelSize="base"
                                            :disabled="true"
                                            :modelValue="true"
                                        />
                                    </ui-layout>
                                </ui-expandable-content>
                            </ui-layout>
                        </apck-settings-panel>

                        <apck-settings-panel title="Alert Limit" :loading="loading">
                            <ui-layout direction="column" gap="2">
                                <ui-input-text
                                    id="active-monitor-alert-cap"
                                    label="Maximum alert emails per hour"
                                    type="number"
                                    :min="1"
                                    :max="200"
                                    :disabled="saving"
                                    class="scan-setting-number"
                                    :modelValue="String(settings.active_monitor_max_alerts_per_hour)"
                                    @update:modelValue="updateSetting('active_monitor_max_alerts_per_hour', Number($event))"
                                />
                                <span class="text--secondary text-sm">
                                    Above this limit, further changes arrive as a single summary
                                    rather than individual emails. Nothing is discarded - every
                                    change is still recorded against your checks and in your
                                    activity log. Integrity failures are always sent individually
                                    and do not count towards this limit.
                                </span>
                            </ui-layout>
                        </apck-settings-panel>

                        <apck-settings-panel title="Allowlist Header" :loading="loading">
                            <ui-layout direction="column" gap="2">
                                <span class="text--secondary text-sm">
                                    If your site is behind a WAF or bot protection, our checks can
                                    be served a challenge page instead of your real page - which
                                    shows up as every script on the page having changed. Our
                                    scanners use dynamic addresses, so allowlisting by IP does not
                                    work. Set a shared secret here and we will send it on every
                                    Active Monitor request, including while recording a sign-in.
                                </span>

                                <ui-input-text
                                    id="active-monitor-request-header"
                                    label="Header value"
                                    type="text"
                                    autocomplete="off"
                                    :disabled="saving"
                                    :modelValue="settings.active_monitor_request_header"
                                    @update:modelValue="updateSetting('active_monitor_request_header', $event)"
                                    description="A random UUID works well - use the button below. Leave empty to send nothing."
                                />

                                <ui-layout align="center" gap="2" wrap>
                                    <ui-button
                                        label="Generate a value"
                                        type="outline"
                                        size="sm"
                                        :disabled="saving"
                                        @click.prevent="generateRequestHeader()"
                                    />
                                    <span class="text--secondary text-sm">
                                        Treat it like a password: anything sending it bypasses the
                                        protection you are configuring.
                                    </span>
                                </ui-layout>

                                <ui-panel-accordion
                                    v-if="settings.active_monitor_request_header"
                                    title="Instructions for your WAF administrator"
                                    title-size="base"
                                >
                                    <ui-layout direction="column" gap="2">
                                        <span class="text--secondary text-sm">
                                            Copy this to whoever administers your WAF. The example is
                                            Cloudflare; the same idea applies to any WAF, and to any
                                            rate limiter or bot protection running at your origin.
                                        </span>
                                        <pre
                                            style="white-space: pre-wrap; word-break: break-word; max-height: 22rem; overflow: auto; font-size: 12px; margin: 0"
                                        ><code>{{ requestHeaderInstructions }}</code></pre>
                                    </ui-layout>
                                </ui-panel-accordion>
                            </ui-layout>
                        </apck-settings-panel>

                        <apck-settings-panel title="Active Monitor Recipients" :loading="loading">
                            <apck-recipient-picker
                                :users="orgUsers"
                                :loading="usersLoading"
                                :disabled="saving"
                                :modelValue="settings.active_monitor_alert_recipient_user_ids"
                                @update:modelValue="setRecipients('active_monitor_alert_recipient_user_ids', $event)"
                            >
                                <template #description>
                                    Select which users should receive Active Monitor alerts. If no
                                    users are selected, no alert emails will be sent - changes are
                                    still recorded and visible on the Active Monitor pages.
                                </template>
                            </apck-recipient-picker>
                        </apck-settings-panel>
                    </ui-panel-base>

                    <!-- ======== DELIVERY TAB ======== -->
                    <ui-panel-base
                        v-if="activeTab === 'delivery'"
                        direction="column"
                        justify="start"
                        align="stretch"
                        gap="2"
                        class="panel-box--tab-content"
                    >
                        <apck-settings-panel title="Delivery Frequency" :loading="loading">
                            <ui-layout direction="column" gap="2">
                                <ui-heading tag="span" color="secondary">How often should alerts be sent?</ui-heading>
                                <ui-layout align="center" wrap>
                                    <ui-button
                                        v-for="opt in deliveryOptions"
                                        :key="opt.value"
                                        :label="opt.label"
                                        :type="settings.delivery_frequency === opt.value ? 'primary' : 'outline'"
                                        :disabled="saving"
                                        @click.prevent="updateSetting('delivery_frequency', opt.value)"
                                    />
                                </ui-layout>
                                <span class="text--secondary text-sm">
                                    Immediate sends each alert as it is detected. Daily and weekly batches
                                    accumulate events into a single digest email.
                                </span>
                            </ui-layout>
                        </apck-settings-panel>

                        <apck-settings-panel title="Alert Recipients" :loading="loading">
                            <apck-recipient-picker
                                :users="orgUsers"
                                :loading="usersLoading"
                                :disabled="saving"
                                :modelValue="settings.alert_recipient_user_ids"
                                @update:modelValue="setRecipients('alert_recipient_user_ids', $event)"
                            >
                                <template #description>
                                    Select which users should receive Trust Monitor alerts.
                                    If no users are selected, no alert emails will be sent.
                                </template>
                            </apck-recipient-picker>
                        </apck-settings-panel>

                        <apck-settings-panel title="Process Events" :loading="loading">
                            <ui-layout direction="column" gap="2">
                                <span class="text--secondary" style="font-size: 13px;">
                                    Re-detect and immediately deliver all pending trust monitor events.
                                    Use this to verify your alert pipeline is working end-to-end,
                                    or to re-trigger delivery after changing settings.
                                </span>
                                <ui-layout>
                                    <ui-button
                                        label="Process Events Now"
                                        iconLeft="fa-bolt"
                                        type="outline"
                                        :disabled="saving || processEventsProcessing"
                                        :loading="processEventsProcessing"
                                        @click.prevent="processEventsNow"
                                    />
                                </ui-layout>
                            </ui-layout>
                        </apck-settings-panel>
                    </ui-panel-base>

                    <!-- ======== WEBHOOK TAB ======== -->
                    <ui-panel-base
                        v-if="activeTab === 'webhook'"
                        direction="column"
                        justify="start"
                        align="stretch"
                        gap="2"
                        class="panel-box--tab-content"
                    >
                        <apck-settings-panel title="Outbound Webhook" :loading="loading">
                            <ui-input-checkbox
                                label="Enable Webhook"
                                description="POST a JSON payload to an external URL for every batch of trust events"
                                labelSize="base"
                                :disabled="saving"
                                :modelValue="settings.webhook_enabled"
                                @update:modelValue="updateSetting('webhook_enabled', $event)"
                            />

                            <ui-expandable-content
                                :open="settings.webhook_enabled"
                                id="webhookSettings"
                                contentId="webhookSettings_content"
                            >
                                <ui-layout direction="column" gap="4">
                                    <ui-layout direction="column" gap="2">
                                        <label>Webhook URL</label>
                                        <input
                                            type="url"
                                            class="form-control"
                                            placeholder="https://example.com/webhook"
                                            :value="settings.webhook_url"
                                            :disabled="saving"
                                            @input="updateSetting('webhook_url', $event.target.value)"
                                        />
                                    </ui-layout>

                                    <ui-layout direction="column" gap="2">
                                        <ui-heading tag="span" color="secondary">Authentication Type</ui-heading>
                                        <ui-layout align="center" wrap>
                                            <ui-button
                                                v-for="opt in authTypeOptions"
                                                :key="opt.value"
                                                :label="opt.label"
                                                :type="settings.webhook_auth_type === opt.value ? 'primary' : 'outline'"
                                                :disabled="saving"
                                                @click.prevent="updateSetting('webhook_auth_type', opt.value)"
                                            />
                                        </ui-layout>
                                    </ui-layout>

                                    <ui-layout direction="column" gap="2">
                                        <label>
                                            Secret / Token
                                            <span v-if="settings.webhook_secret_set" class="text--secondary text-sm">(currently set)</span>
                                        </label>
                                        <input
                                            type="password"
                                            class="form-control"
                                            :placeholder="settings.webhook_secret_set ? '••••••••' : 'Enter secret'"
                                            :value="settings.webhook_secret"
                                            :disabled="saving"
                                            @input="updateSetting('webhook_secret', $event.target.value)"
                                        />
                                        <span class="text--secondary text-sm">
                                            For HMAC: the request body is signed with this key and the signature
                                            is sent in the <code>X-AppCheck-Signature</code> header.<br/>
                                            For Bearer: the token is sent in the <code>Authorization</code> header.
                                        </span>
                                    </ui-layout>

                                    <ui-button
                                        label="Send Test Webhook"
                                        iconLeft="fa-paper-plane"
                                        type="outline"
                                        :disabled="saving || webhookTesting || !settings.webhook_url"
                                        :loading="webhookTesting"
                                        @click.prevent="testWebhook"
                                    />
                                </ui-layout>
                            </ui-expandable-content>
                        </apck-settings-panel>
                    </ui-panel-base>

                    <!-- ======== SUPPRESSION TAB ======== -->
                    <ui-panel-base
                        v-if="activeTab === 'suppression'"
                        direction="column"
                        justify="start"
                        align="stretch"
                        gap="2"
                        class="panel-box--tab-content"
                    >
                        <apck-settings-panel title="Suppression" :loading="loading">
                            <ui-input-checkbox
                                label="Enable Suppression Checking"
                                description="When enabled, hostnames in the suppression list will be excluded from Trust Monitor processing and a log entry will be recorded"
                                labelSize="base"
                                :disabled="saving"
                                :modelValue="settings.enable_suppression"
                                @update:modelValue="updateSetting('enable_suppression', $event)"
                            />

                            <ui-layout align="center" gap="2">
                                <ui-button
                                    label="Manage Suppression Lists"
                                    type="outline"
                                    iconLeft="fa-ban"
                                    url="/trust/suppression/manage"
                                />
                            </ui-layout>
                        </apck-settings-panel>

                        <apck-settings-panel title="Strict Scope Processing" :loading="loading">
                            <ui-input-checkbox
                                label="Enable Strict Scope Processing"
                                description="When enabled, only domains whose apex domain matches one of your Trust Scope entries will be processed. Third parties and vulnerabilities for out-of-scope domains are skipped and a log entry is recorded."
                                labelSize="base"
                                :disabled="saving"
                                :modelValue="settings.enable_strict_scope_processing"
                                @update:modelValue="updateSetting('enable_strict_scope_processing', $event)"
                            />
                        </apck-settings-panel>

                    </ui-panel-base>

                    <!-- ======== SCAN SETTINGS TAB ======== -->
                    <ui-panel-base
                        v-if="activeTab === 'scan'"
                        direction="column"
                        justify="start"
                        align="stretch"
                        gap="2"
                        class="panel-box--tab-content"
                    >
                        <apck-settings-panel title="OSINT Scanning" :loading="loading">
                            <ui-input-checkbox
                                label="Enable OSINT Scanning"
                                description="Enable scheduled Trust Monitor OSINT scans for this organisation. When enabled, scans will run automatically at the configured interval and OSINT Discovery is automatically activated."
                                labelSize="base"
                                :disabled="saving"
                                :modelValue="settings.enable_osint_scanning"
                                @update:modelValue="updateSetting('enable_osint_scanning', $event)"
                            />
                        </apck-settings-panel>

                        <template v-if="settings.enable_osint_scanning">
                        <apck-settings-panel title="Scan Schedule" :loading="loading">
                            <ui-layout direction="column" gap="2">
                                <ui-heading tag="span" color="secondary">Repeat Interval</ui-heading>
                                <span class="text--secondary text-sm">
                                    How often standalone Trust Monitor OSINT scans should run.
                                    These scans discover and assess third-party trust relationships
                                    using OSINT techniques without requiring a full web application scan.
                                </span>
                                <ui-layout align="center" wrap>
                                    <ui-button
                                        v-for="opt in (settings.scan_repeat_options || []).filter(function(o) { return o.value !== ''; })"
                                        :key="opt.value"
                                        :label="opt.label"
                                        :type="settings.scan_repeat_interval === opt.value ? 'primary' : 'outline'"
                                        :disabled="saving"
                                        @click.prevent="updateSetting('scan_repeat_interval', opt.value)"
                                    />
                                </ui-layout>
                                <span v-if="settings.next_scan_at" class="text--secondary text-sm">
                                    Next scan estimated: {{ new Date(settings.next_scan_at).toLocaleString() }}
                                </span>
                            </ui-layout>

                            <ui-layout direction="column" gap="2">
                                <ui-heading tag="span" color="secondary">Scan Windows</ui-heading>
                                <span class="text--secondary text-sm">
                                    Optionally restrict when scheduled OSINT scans can run.
                                    If no windows are defined, scans run whenever they are due.
                                </span>
                                <apck-schedule-window-editor
                                    :modelValue="settings.scan_schedule_times"
                                    :disabled="saving"
                                    @update:modelValue="updateSetting('scan_schedule_times', $event)"
                                />
                            </ui-layout>
                        </apck-settings-panel>

                        <apck-settings-panel title="OSINT Discovery" :loading="loading">
                            <ui-input-checkbox
                                label="Include DNS Bruteforce"
                                description="When DNS Enumeration is enabled, include DNS hostname guessing based on a wordlist of common hostnames."
                                labelSize="base"
                                :disabled="saving"
                                :modelValue="settings.include_dns_bruteforce"
                                @update:modelValue="updateSetting('include_dns_bruteforce', $event)"
                            />
                            <ui-input-checkbox
                                label="Enable All OSINT Sources"
                                description="Enable all available OSINT sources. Note that some are experimental."
                                labelSize="base"
                                :disabled="saving"
                                :modelValue="settings.enable_all_osint_sources"
                                @update:modelValue="updateSetting('enable_all_osint_sources', $event)"
                            />
                        </apck-settings-panel>

                        <apck-settings-panel title="Crawl Settings" :loading="loading">
                            <ui-input-checkbox
                                label="Apply to All HTTP Responses"
                                description="Run Trust Monitor analysis against all responses during the crawling phase."
                                labelSize="base"
                                :disabled="saving"
                                :modelValue="settings.apply_to_all_http_responses"
                                @update:modelValue="updateSetting('apply_to_all_http_responses', $event)"
                            />
                            <ui-input-text
                                label="Max OSINT Hosts"
                                description="Maximum number of subdomains to enumerate via DNS/OSINT techniques."
                                type="number"
                                :min="1"
                                :max="10000"
                                :disabled="saving"
                                class="scan-setting-number"
                                :modelValue="String(settings.max_osint_hosts)"
                                @update:modelValue="updateSetting('max_osint_hosts', Number($event))"
                            />
                            <ui-input-text
                                label="Crawl Depth"
                                description="Maximum depth of Trust Map crawl."
                                type="number"
                                :min="1"
                                :max="10000"
                                :disabled="saving"
                                class="scan-setting-number"
                                :modelValue="String(settings.trustmonitor_crawl_depth)"
                                @update:modelValue="updateSetting('trustmonitor_crawl_depth', Number($event))"
                            />
                            <ui-input-text
                                label="Crawl Max Time (seconds)"
                                description="Maximum time in seconds for the initial discovery crawl."
                                type="number"
                                :min="60"
                                :max="7200"
                                :disabled="saving"
                                class="scan-setting-number"
                                :modelValue="String(settings.trustmonitor_crawl_maxtime)"
                                @update:modelValue="updateSetting('trustmonitor_crawl_maxtime', Number($event))"
                            />
                        </apck-settings-panel>
                        </template>
                    </ui-panel-base>
                </ui-layout>
            </template>
        </ui-page>
    `
};
